// Core
import React, {
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../hooks/useBreakpoint';

// Components
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType,
} from '../Button';
import { Col } from '../Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';
import { Row } from '../Row';
import { ArrowLeft, Close } from '../../icons';

// Component
import {
  ModalHeight,
  ModalPanelProps,
  ModalSize,
  ModalWidth,
} from './ModalPanel.types';
import {
  BackButtonContainer,
  CloseButtonContainer,
  Container,
  Content,
  DescriptionContainer,
  GradientEffect,
  Header,
  HeaderContent,
  TitleContainer,
} from './ModalPanel.style';

function ModalPanel({
  children,
  className,
  customHeader,
  description,
  getContainer,
  onBack = false,
  onClose,
  showModal = false,
  size = ModalSize.lg,
  testId,
  title,
}: ModalPanelProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs, isSm, isMd } = useBreakpoint();

  // Ref
  const contentRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

  /* ***********************************************************************************************
  *************************************** LOCAL STATE  *********************************************
  *********************************************************************************************** */

  interface State {
    dimensions: {
      width: ModalWidth;
      height: ModalHeight
    };
    hasScroll: boolean;
    showGradient: boolean;
  }

  const initialState: State = {
    dimensions: {
      width: '85%',
      height: 'auto',
    },
    hasScroll: false,
    showGradient: false,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ************************************* COMPONENT HANDLING *****************************************
  *********************************************************************************************** */

  const getDimensionsFromModalSize = (
    modalSize: ModalSize,
  ): { width: ModalWidth; height: ModalHeight } => {
    let dimensions: { width: ModalWidth; height: ModalHeight };

    if (modalSize === ModalSize.xl) {
      dimensions = { width: '98%', height: '96%' };
    } else if (modalSize === ModalSize.sm) {
      dimensions = { width: '40%', height: 'auto' };
    } else {
      dimensions = { width: '85%', height: 'auto' };
    }

    return dimensions;
  };

  useEffect(() => {
    setState((prevState: State) => ({
      ...prevState,
      dimensions: getDimensionsFromModalSize(size),
    }));
  }, [size]);

  useEffect(() => {
    if (showModal) {
      const originalBodyOverflow: string = document.body.style.overflow;
      const originalHtmlOverflow: string = document.documentElement.style.overflow;

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }

    return undefined;
  }, [showModal]);

  /* Sets up a scroll listener on the element. */
  useEffect(() => {
    const scroll: HTMLElement | null = contentRef.current;
    let cleanup: (() => void) | undefined;

    const handleScroll = (): void => {
      if (scroll) {
        const isScrollable: boolean = scroll.scrollHeight > scroll.clientHeight + 2;
        const currentScroll: number = scroll.scrollTop + scroll.clientHeight;
        const isAtBottom: boolean = currentScroll >= scroll.scrollHeight - 2;

        setState((prevState: State) => ({
          ...prevState,
          hasScroll: isScrollable,
          showGradient: isScrollable && !isAtBottom,
        }));
      }
    };

    if (scroll) {
      // Initial check
      handleScroll();

      // Add event listeners
      scroll.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);

      // Create observer to detect content changes that might affect scroll
      const resizeObserver: ResizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(scroll);

      cleanup = (): void => {
        scroll.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        resizeObserver.disconnect();
      };
    }

    return cleanup;
  }, [showModal]); // Revalidate when modal opens/closes

  return (
    <Container
      centered
      className={className}
      closeIcon={false}
      data-testid={testId}
      footer={false}
      getContainer={getContainer}
      onCancel={onClose}
      open={showModal}
      width={state.dimensions.width}
      height={state.dimensions.height}
    >
      <Header description={description}>
        <HeaderContent hasBackButton={onBack === false}>
          {onBack && (
            <BackButtonContainer>
              <Button
                onClick={onBack as MouseEventHandler<HTMLElement>}
                type={ButtonType.link}
                size={ButtonSize.xs}
                block={false}
                color={ButtonColor.neutral}
              >
                <ArrowLeft />
                {t('common.back')}
              </Button>
            </BackButtonContainer>
          )}
          {customHeader || (
            title !== undefined && (
              <TitleContainer>
                <Paragraph
                  className="modal-title"
                  size={ParagraphSize.xl}
                  weight={ParagraphWeight.bold}
                >
                  {title}
                </Paragraph>
              </TitleContainer>
            )
          )}
          {description && (
            <DescriptionContainer>
              <Row>
                <Col>
                  <Paragraph className="modal-header-description">
                    {description}
                  </Paragraph>
                </Col>
              </Row>
            </DescriptionContainer>
          )}
        </HeaderContent>
        <CloseButtonContainer>
          <Button
            type={ButtonType.secondary}
            square
            size={(isXs || isSm || isMd) ? ButtonSize.xs : ButtonSize.sm}
            onClick={onClose}
            className="close-trendings-button"
          >
            <Close />
          </Button>
        </CloseButtonContainer>
      </Header>

      <Content ref={contentRef} hasScroll={state.hasScroll}>
        {children}
      </Content>

      {state.hasScroll && state.showGradient && (
        <GradientEffect
          data-testid="gradient-effect-modal"
          isHidden={false}
        />
      )}
    </Container>
  );
}

export { ModalPanel };
